import { ErrorInfo } from '../models/ForecastTimelineData';
import { apiClient } from './apiClient';

/**
 * Interface for individual fog prediction in the METAR aggregated response
 */
interface MetarFogPrediction {
  id?: number;
  location_id: number;
  horizon: number;
  predicted_at: string;
  generated_at: string;
  fog_predicted: boolean;
  fog_prediction_confidence?: number;
  visibility_threshold: number;
  prediction_threshold?: number;
  model_name?: string;
  location: string;
  time: string;
  predicted_fog: boolean;
}

/**
 * Interface for individual fog actual in the METAR aggregated response
 */
interface MetarFogActual {
  id?: number;
  location_id: number;
  forecast_time: string;
  visibility_level: number;
  source: string;
  location: string;
}

/**
 * Interface for aggregated predictions segment (1-hour intervals for METAR)
 */
interface MetarAggregatedPredictions {
  location: number;
  segment_time: string;
  horizon: number;
  predictions: MetarFogPrediction[];
  fog_predicted: boolean;
  lowest_visibility_level: number | null;
  normalized_confidence?: number | null;
}

/**
 * Interface for aggregated actuals segment (30-minute intervals for METAR)
 */
interface MetarAggregatedActuals {
  location: number;
  segment_time: string;
  fog_actuals: MetarFogActual[];
  available_visibility_thresholds: number[];
  fog_predicted: boolean;
  lowest_visibility_level: number | null;
  highest_visibility_level?: number | null;
  visibility_median: number | null;
  visibility_mean: number | null;
  visibility_hmean: number | null;
}

/**
 * Interface for the complete METAR fog forecast response
 */
interface MetarFogForecastResponse {
  requested_time: string;
  aggregated_predictions_30min: MetarAggregatedPredictions[];
  aggregated_predictions_hourly: MetarAggregatedPredictions[];
  aggregated_actuals: MetarAggregatedActuals[];
  errors?: ErrorInfo[];
  has_errors?: boolean;
  has_warnings?: boolean;
  missing_data_errors?: any[];
}

/**
 * Interface for batch METAR forecast result with failure tracking
 */
export interface BatchMetarFogForecastResult {
  forecasts: MetarFogForecastResponse[];
  requestedCount: number;
  successCount: number;
  failedCount: number;
  hasPartialFailure: boolean;
}

/**
 * Service for fetching METAR-based fog forecasts from the API
 * Used for UK locations with 30-minute actuals and 1-hour predictions
 */
class MetarFogForecastService {
  constructor() {
    // Service initialized
  }

  /**
   * Split an array into balanced chunks with smaller chunks first
   * Example: 21 items with chunkSize 5 → [4,4,4,4,5] instead of [5,5,5,5,1]
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    if (array.length === 0) return [];

    const numChunks = Math.ceil(array.length / chunkSize);
    const baseSize = Math.floor(array.length / numChunks);
    const remainder = array.length % numChunks;

    const chunks: T[][] = [];
    let currentIndex = 0;

    for (let i = 0; i < numChunks; i++) {
      const currentChunkSize = i < (numChunks - remainder) ? baseSize : baseSize + 1;
      chunks.push(array.slice(currentIndex, currentIndex + currentChunkSize));
      currentIndex += currentChunkSize;
    }

    return chunks;
  }

  /**
   * Fetches METAR fog forecasts for multiple locations using parallel chunked requests
   * @param locations Array of location IDs
   * @param customNow Optional custom date to use instead of current time
   * @param onChunkReceived Optional callback invoked as each chunk completes (for progressive UI updates)
   * @param chunkSize Number of locations to fetch per chunk (default: 5)
   * @returns A promise resolving to batch forecast result with failure tracking
   */
  public async getBatchMetarFogForecast(
    locations: number[],
    customNow?: Date | null,
    onChunkReceived?: (chunk: MetarFogForecastResponse[]) => void,
    chunkSize: number = 5
  ): Promise<BatchMetarFogForecastResult> {
    try {
      // Get requested time - use custom date if provided, otherwise use current time
      const requestedTime = customNow ? customNow.toISOString() : new Date().toISOString();

      // Split locations into chunks
      const chunks = this.chunkArray(locations, chunkSize);

      // Fetch all chunks in parallel using Promise.allSettled for partial failure handling
      const allForecasts: MetarFogForecastResponse[] = [];
      let failedChunks = 0;

      const results = await Promise.allSettled(
        chunks.map(async (chunk, chunkIndex) => {
          // Build query params for this chunk
          const params = new URLSearchParams();
          chunk.forEach(loc => params.append('locations', loc.toString()));
          params.append('requested_time', requestedTime);

          // Make the API request for this chunk
          const response: { forecasts: MetarFogForecastResponse[] } = await apiClient.get(
            `/api/fog-forecast-metar/batch?${params.toString()}`
          );

          // Add to results
          allForecasts.push(...response.forecasts);

          // Call callback if provided (for progressive UI updates)
          if (onChunkReceived) {
            try {
              onChunkReceived(response.forecasts);
            } catch (callbackError) {
              console.error(`Error in onChunkReceived callback for METAR chunk ${chunkIndex + 1}:`, callbackError);
            }
          }

          return response.forecasts.length;
        })
      );

      // Log any failed chunks
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedChunks++;
          console.error(`METAR Chunk ${index + 1}/${chunks.length} failed:`, result.reason);
        }
      });


      return {
        forecasts: allForecasts,
        requestedCount: locations.length,
        successCount: allForecasts.length,
        failedCount: locations.length - allForecasts.length,
        hasPartialFailure: failedChunks > 0
      };
    } catch (error) {
      console.error('Error fetching batch METAR fog forecast:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const metarFogForecastService = new MetarFogForecastService();

// Export the service instance and types
export { metarFogForecastService };
export type { MetarFogForecastResponse, MetarAggregatedPredictions, MetarAggregatedActuals, MetarFogActual, MetarFogPrediction };
